import { callable } from './callable.js'
import type { FormField, FormLogic, FormTheme } from '../types/forms'

export interface QuizKeyDefinition {
  correct: unknown
  tolerance?: number
  points: number
  explanation?: string
}

export interface SetEduQuizInput {
  workspaceId: string
  checklistId: string
  formId?: string
  passPercent?: number
  maxAttempts?: number
  allowKeyEditsAfterAttempts?: boolean
  keys?: Record<string, QuizKeyDefinition>
  clear?: boolean
}

export interface SetEduQuizResult {
  success: boolean
  checklistId: string
  formId?: string
  cleared?: boolean
}

export interface GetEduQuizSetupInput {
  workspaceId: string
  checklistId: string
}

export type GetEduQuizSetupResult =
  | { quiz: null }
  | {
      formId: string
      passPercent: number
      maxAttempts: number
      allowKeyEditsAfterAttempts: boolean
      keys: Record<string, QuizKeyDefinition>
      attemptCount: number
      keysLocked: boolean
      closed: boolean
      closedAt: unknown
    }

export interface CloseEduQuizInput {
  workspaceId: string
  checklistId: string
}

export interface CloseEduQuizResult {
  success: boolean
  checklistId: string
  closedAt: unknown
  alreadyClosed?: boolean
}

export async function setEduQuiz(input: SetEduQuizInput): Promise<SetEduQuizResult> {
  return callable<SetEduQuizInput, SetEduQuizResult>('setEduQuiz', input)
}

export async function getEduQuizSetup(input: GetEduQuizSetupInput): Promise<GetEduQuizSetupResult> {
  return callable<GetEduQuizSetupInput, GetEduQuizSetupResult>('getEduQuizSetup', input)
}

export async function closeEduQuiz(input: CloseEduQuizInput): Promise<CloseEduQuizResult> {
  return callable<CloseEduQuizInput, CloseEduQuizResult>('closeEduQuiz', input)
}

export interface GetEduQuizInput {
  workspaceId: string
  checklistId: string
  learnerId: string
}

export interface QuizBestResult {
  score: number
  maxScore: number
  percent: number
  passed: boolean
  attemptNo: number
}

export interface QuizReviewPerField {
  correct: boolean
  points?: number
  score?: number
}

export interface QuizReview {
  attemptNo: number
  answers: Record<string, unknown>
  perField: Record<string, QuizReviewPerField>
}

export interface GetEduQuizResult {
  formId: string
  fields: FormField[]
  form: {
    title?: string
    description?: string
    logic?: FormLogic
    theme?: FormTheme
  }
  settings: {
    passPercent: number
    maxAttempts: number
    dueDate: string
    maxScore: number
  }
  attemptsUsed: number
  attemptsLeft: number
  bestResult: QuizBestResult | null
  keys: Record<string, QuizKeyDefinition> | null
  closed: boolean
  review: QuizReview | null
}

export interface SubmitEduQuizAttemptInput {
  workspaceId: string
  checklistId: string
  learnerId: string
  answers: Record<string, unknown>
}

export interface SubmitEduQuizAttemptResult {
  score: number
  maxScore: number
  percent: number
  passed: boolean
  attemptNo: number
}

export async function getEduQuiz(input: GetEduQuizInput): Promise<GetEduQuizResult> {
  return callable<GetEduQuizInput, GetEduQuizResult>('getEduQuiz', input)
}

export async function submitEduQuizAttempt(
  input: SubmitEduQuizAttemptInput,
): Promise<SubmitEduQuizAttemptResult> {
  return callable<SubmitEduQuizAttemptInput, SubmitEduQuizAttemptResult>('submitEduQuizAttempt', input)
}
